'use client';
import { useState } from 'react';
import { Route } from 'next';
import { getCsrfToken } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '~/components/ui/Button';
import { Input } from '~/components/ui/input';
import { useToast } from '~/components/ui/use-toast';
import { WebAuthError } from '~/lib/errors';
import { cn } from '~/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormControl } from '@mui/material';
import { GoogleIcon, SpinnerIcon } from '../../components/assets/icons';
import { BASE_URL } from '../../config';
import { AccountType } from '../../types/account_type';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';

interface SignInOptions {
  // Either the account ID or "developer".
  readonly accountID: string;
  readonly callbackURL?: string | undefined;
}

const signIn = async ({ accountID, callbackURL = window.location.href }: SignInOptions) => {
  const csrfToken = await getCsrfToken();

  const response = await fetch(`${BASE_URL}/api/auth/signin/${accountID}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      csrfToken: csrfToken ?? '',
      callbackUrl: callbackURL,
      json: 'true',
    }),
  });

  try {
    const data: { readonly url?: string | undefined } | undefined = await response.json();
    return data?.url ?? callbackURL;
  } catch (e) {
    throw new WebAuthError('Sign in failed', { cause: e });
  }
};

const formSchema = z.object({
  accountID: z.string().optional(),
  sso: z.enum(['true', 'false']).optional(),
});

export interface LoginFormProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly callbackUrl: string;
  readonly error?: string | undefined;
}

export function LoginForm({ callbackUrl, className, ...props }: LoginFormProps) {
  const router = useRouter();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountID: '',
    },
  });

  const [showSSOLogin, setShowSSOLogin] = useState(false);

  const developerUrl = `${BASE_URL}/register/checkDeveloper?callbackURL=${encodeURIComponent(callbackUrl)}`;

  const onSubmit = (loginType: 'sso' | 'developer') => {
    return async (values: z.infer<typeof formSchema>) => {
      let redirectUrl = callbackUrl;
      if (loginType === 'developer') {
        redirectUrl = developerUrl;
      }

      try {
        const url = await signIn({
          accountID: loginType === 'sso' ? values.accountID || '' : AccountType.Developer.toLowerCase(),
          callbackURL: redirectUrl,
        });

        router.push(url as Route<string>);
      } catch (e) {
        form.setError('accountID', {
          message:
            'Error: Only business and enterprise accounts configured with Custom SSO can sign in here. All other accounts should use "Login With Google".',
        });

        const description = !(e instanceof WebAuthError) && e instanceof Error ? e.message : undefined;
        toast({ variant: 'destructive', title: 'Error submitting login form', description });
      }
    };
  };
  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit('developer'))} className="space-y-8">
          <input {...form.register('sso', { value: 'false' })} type="hidden" />

          <Button variant="outline" type="submit" disabled={form.formState.isSubmitting} className="w-full">
            {form.formState.isSubmitting ? (
              <SpinnerIcon className="animate-spin-slow mr-2 h-4 w-4" />
            ) : (
              <GoogleIcon className="mr-2 h-4 w-4" />
            )}
            Login With Google
          </Button>
        </form>
      </Form>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background text-muted-foreground px-2">Or</span>
        </div>
      </div>
      {showSSOLogin ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit('sso'))} className="space-y-8">
            <input {...form.register('sso', { value: 'true' })} type="hidden" />
            <FormField
              control={form.control}
              name="accountID"
              render={({ field }) => (
                <FormItem className="w-full ">
                  <FormControl className="w-full ">
                    <FormLabel>Account ID</FormLabel>
                    <Input className="mt-2 w-full" placeholder="my-company" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full">
              Continue
            </Button>
          </form>
        </Form>
      ) : (
        <Button
          variant="outline"
          type="button"
          disabled={form.formState.isSubmitting}
          className="w-full"
          onClick={() => setShowSSOLogin(true)}
        >
          Custom SSO Login
        </Button>
      )}
    </div>
  );
}
